import { Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { copyTextToClipboard } from "@/lib/clipboard";
import { cn } from "@/lib/utils";

export const COPY_MARKDOWN_LABEL = "Copy as Markdown";

/**
 * The action row under an assistant answer.
 *
 * Copy writes the answer's Markdown source, not the answer as it was rendered. Selecting the
 * transcript in the browser yields rendered text, which has already lost the table pipes, the
 * task-list boxes and the `==highlight==` marks an eval compares against. What this writes is the
 * text the run persisted, so an observed output pasted into an eval is the same string the model
 * produced — reserved evidence and mention links included, in their Markdown form.
 *
 * The whole row is gated, not just the button inside it, so that a flag that is off leaves
 * nothing in the tree at all. It renders nothing while the flag is pending too — pending is not
 * denial for a route gate, but an affordance that appears a beat late reads better than one that
 * flickers away.
 *
 * The row carries no placement of its own: where it sits, and whether it sits in the flow at all,
 * is the caller's `className`.
 */
export function AssistantMessageActions({
  markdown,
  className,
}: {
  markdown: string;
  className?: string;
}) {
  const { flags } = useFeatureFlags();
  if (!flags.chat_copy_markdown) return null;
  if (markdown.trim().length === 0) return null;

  return (
    <div className={cn("flex items-center gap-1", className)}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={COPY_MARKDOWN_LABEL}
        data-testid="assistant-copy-markdown"
        // A 28px glyph with a 44px target, the way `Checkbox`, `Switch` and `Sidebar` draw one:
        // `after:-inset-2` grows the hit area by 8px on every side and the layout box not at
        // all. It is unconditional on purpose. `index.css` exempts any button carrying a `size-`
        // from the automatic touch minimum, and the `touch:` variant would not have covered a
        // hybrid device — a touchscreen laptop reports its mouse as the primary pointer, so it
        // matches `no-touch:` while a finger is still what reaches for this.
        className="relative text-neutral-dim after:absolute after:-inset-2"
        onClick={() => {
          void copyTextToClipboard(markdown, {
            success: "Markdown copied",
            failure: "Could not copy Markdown",
          });
        }}
      >
        <Copy className="size-3.5" />
      </Button>
    </div>
  );
}
